import React, { useEffect, useState } from "react";
import { Container, Row, Col, Button } from "reactstrap";
import { getOnboardingSteps, finishOnboarding } from "helpers/fakebackend_helper";
import { useParams } from "react-router-dom";
import EmailFormStep from "./onboarding-steps/EmailFormStep";
import SignPDFFormStep from "./onboarding-steps/SignPDFFormStep";
import FinishOnboardingStep from "./onboarding-steps/FinishOnboardingStep";
import PhoneNumberStep from "./onboarding-steps/PhoneNumberStep";
import AreYouUSCitizenStep from "./onboarding-steps/AreYouUSCitizenStep";
import FilingStatusStep from "./onboarding-steps/FilingStatusStep";
import DynamicCameraOcrStep from "./onboarding-steps/DynamicCameraOcrStep";

const Onboarding = () => {
  document.title = "Onboarding | CurbDash";
  const { token } = useParams()

  const [steps, setSteps] = useState([]);
  const [authorized, setAuthorized] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [email, setEmail] = useState("");

  useEffect(() => {
    loadSteps();
  }, []);

  const loadSteps = async () => {
    try {
      const res = await getOnboardingSteps(token);

      if (!res || res.code !== 1 || !Array.isArray(res.data)) {
        setAuthorized(false);
        return;
      }

      const sortedSteps = res.data.sort(
        (a, b) => Number(a.step_order) - Number(b.step_order)
      );

      setSteps(sortedSteps);

    } catch (e) {
      setAuthorized(false);
    }
  };

  const backgroundStyle = {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #ffffff, #f0f0f0)",
  };

  const buttonStyle = {
    padding: "1rem",
    fontSize: "1.2rem",
    fontWeight: 600,
    background: "linear-gradient(135deg, #1e1e1e, #4a4a4a)",
    border: "none",
  };

  const checkIfLastStep = async (step) => {
    // Find the highest step order
    const maxOrder = Math.max(...steps.map(s => Number(s.step_order)));

    // Determine the second-to-last step order
    const secondToLastOrder = maxOrder - 1;

    // If the current step is the second-to-last
    if (Number(step.step_order) === secondToLastOrder) {
      await finishOnboarding(token, { email });
    }
  }

  const renderStep = () => {
    const step = steps[currentIndex];
    if (!step) return null;

    switch (step.step_type) {
      case "email_form":
        return (
          <EmailFormStep
            step={step}
            token={token}
            onNext={(emailValue, completedSteps) => {
              setEmail(emailValue);

              const map = {};
              completedSteps.forEach(s => {
                map[s.step_type] = s.is_done;
              });

              // Find first incomplete step
              const nextIndex = steps.findIndex(s => !map[s.step_type]);

              setCurrentIndex(nextIndex === -1 ? 0 : nextIndex);
            }}
          />
        );

      case "driver_license_ocr":
      case "take_a_picture":
      case "choose_citizen_document":
      case "choose_noncitizen_status":
      case "ssn_ocr":
        return (
          <DynamicCameraOcrStep
            step={step}
            token={token}
            email={email}
            onNext={(subPhase) => {
              console.log("MAIN subphase: ", subPhase)
              if (subPhase == "passport" || subPhase === "birth_certificate") {
                setCurrentIndex(i => i + 2)
              }
              else {
                console.log("MAIN else subphase: ", subPhase)
                setCurrentIndex(i => i + 1)
              }
            }}
          />
        );

      case "sign_pdf_arbitration_agreement":
      case "sign_pdf_employee_handbook":
      case "sign_pdf_employee_withholding_allowance_certificate":
      case "sign_pdf_employment_eligibility_verification":
      case "sign_pdf_employees_withholding_certificate":
      case "sign_pdf_notice_to_employee_wage_theft":
      case "sign_pdf_ui_di_pfl":
      case "sign_pdf_nda_confidentiality_agreement":
        return (
          <SignPDFFormStep
            key={step.id}
            step={step}
            email={email}
            token={token}
            onNext={async () => {
              await checkIfLastStep(step)
              setCurrentIndex(i => i + 1);
            }}
          />
        );

      case "enter_phone_number":
        return <PhoneNumberStep
          step={step}
          email={email}
          token={token}
          onNext={() => setCurrentIndex(i => i + 1)}
        />;

      case "are_you_us_citizen":
        return <AreYouUSCitizenStep
          step={step}
          email={email}
          token={token}
          onNext={(isCitizen) => {
            if (isCitizen) {
              setCurrentIndex(i => i + 1)
            }
            else {
              setCurrentIndex(i => i + 2)
            }
          }}
        />;

      case "how_do_you_file_taxes":
        return <FilingStatusStep
          step={step}
          email={email}
          token={token}
          onNext={() => setCurrentIndex(i => i + 1)}
        />;

      case "finish_onboarding":
        return <FinishOnboardingStep step={step} />;

      default:
        return (
          <>
            <h2 className="text-dark mb-5 fw-semibold" style={{ fontSize: "2rem" }}>
              {step.step_title || "Thank you for completing the Curbstand Onboarding!"}
            </h2>

            <h6 className="text-dark mb-5" style={{ fontSize: "1.3rem" }}>
              {step.step_description || "We are excited to start working with you."}
            </h6>

            <div className="d-grid gap-3 mb-3">
              <Button style={buttonStyle} onClick={() => setCurrentIndex(i => i + 1)}>
                NEXT
              </Button>
            </div>
          </>
        );
    }
  };

  // UNAUTHORIZED VIEW
  if (!authorized) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={backgroundStyle}>
        <Container>
          <Row className="justify-content-center text-center">
            <Col md={6}>
              <img
                src="/cs-logo-circle.png"
                alt="Curbstand Logo"
                style={{ width: "60%", maxWidth: 140, marginBottom: 30 }}
              />

              <h2 className="text-dark mb-4 fw-semibold">You are not authorized.</h2>

              <img
                src="/cs_horizontal_logo.webp"
                alt="Curbstand"
                style={{ height: 40 }}
              />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // LOADING STATE
  if (steps.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={backgroundStyle}>
        <h4>Loading...</h4>
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center justify-content-center" style={backgroundStyle}>
      <Container>
        <Row className="justify-content-center text-center">
          <Col md={9}>
            <img
              src="/cs-logo-circle.png"
              alt="Curbstand Logo"
              style={{ width: "60%", maxWidth: 140, marginBottom: 30 }}
            />

            {renderStep()}

            {/* Footer Logo */}
            <div className="d-flex align-items-center justify-content-center mt-4">
              <a href="https://www.curbstand.com/" target="_blank" rel="noreferrer">
                <img
                  src="/cs_horizontal_logo.webp"
                  alt="Curbstand"
                  style={{ height: 28 }}
                />
              </a>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Onboarding;